import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { hashPassword, verifyPassword, encrypt, decrypt } from '@/lib/crypto';
import {
  hasExistingAccount, getPasswordHash, setPasswordHash, loadData, saveData,
  hasPinSetup, getPinHash, setPinHash, getPinEncryptedPassword, setPinEncryptedPassword, clearPin,
  hasBiometricSetup, getBiometricCredentialId, setBiometricCredentialId,
  getBiometricEncryptedPassword, setBiometricEncryptedPassword, getBiometricKey, setBiometricKey, clearBiometric,
  getStoredEmail, setStoredEmail,
} from '@/lib/storage';
import { registerBiometric, authenticateBiometric } from '@/lib/webauthn';
import { AppData, DEFAULT_APP_DATA } from '@/lib/types';
import { processAutoContributions } from '@/lib/savings';

interface AuthContextType {
  isSetup: boolean;
  isUnlocked: boolean;
  data: AppData;
  unlock: (value: string, isPin?: boolean) => Promise<boolean>;
  unlockBiometric: () => Promise<boolean>;
  setup: (email: string, password: string) => Promise<void>;
  login: (email: string, pwd: string) => Promise<void>;
  lock: () => void;
  updateData: (updater: (prev: AppData) => AppData) => Promise<void>;
  password: string;
  setupPin: (pin: string) => Promise<void>;
  removePin: () => void;
  hasPin: boolean;
  setupBiometric: () => Promise<void>;
  removeBiometric: () => void;
  hasBio: boolean;
  supabaseSession: Session | null;
  storedEmail: string;
  updateStoredEmail: (newEmail: string) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isSetup, setIsSetup] = useState(hasExistingAccount());
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [data, setData] = useState<AppData>(DEFAULT_APP_DATA);
  const [password, setPassword] = useState('');
  const [hasPin, setHasPin] = useState(hasPinSetup());
  const [hasBio, setHasBio] = useState(hasBiometricSetup());
  const [supabaseSession, setSupabaseSession] = useState<Session | null>(null);
  const [storedEmail, setStoredEmailState] = useState(getStoredEmail());
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const autoRestoreAttempted = useRef(false);

  // Listen for Supabase auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSupabaseSession(session);
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSupabaseSession(session);
    });
    return () => subscription.unsubscribe();
  }, []);


  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (data.settings.autoLockMinutes > 0 && isUnlocked) {
      timerRef.current = setTimeout(() => {
        sessionStorage.removeItem('_ab_session');
        setIsUnlocked(false);
        setPassword('');
      }, data.settings.autoLockMinutes * 60 * 1000);
    }
  }, [data.settings.autoLockMinutes, isUnlocked]);

  // Auto-lock on activity
  useEffect(() => {
    if (!isUnlocked) return;
    const handler = () => resetTimer();
    window.addEventListener('click', handler);
    window.addEventListener('keydown', handler);
    window.addEventListener('touchstart', handler);
    resetTimer();
    return () => {
      window.removeEventListener('click', handler);
      window.removeEventListener('keydown', handler);
      window.removeEventListener('touchstart', handler);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isUnlocked, resetTimer]);

  // Pause timer when app goes to background, resume when it comes back
  useEffect(() => {
    const handler = () => {
      if (document.hidden) {
        // Pause: clear the timer so it doesn't fire while in background
        if (timerRef.current) clearTimeout(timerRef.current);
      } else if (isUnlocked) {
        // Resume: restart the full inactivity countdown
        resetTimer();
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [isUnlocked, resetTimer]);

  const unlockWithPassword = useCallback(async (pwd: string): Promise<boolean> => {
    const hash = getPasswordHash();
    if (!hash) return false;
    const valid = await verifyPassword(pwd, hash);
    if (!valid) return false;
    try {
      const loaded = await loadData(pwd);
      const processed = processAutoContributions(loaded);
      if (processed !== loaded) await saveData(processed, pwd);
      setData(processed);
      setPassword(pwd);
      sessionStorage.setItem('_ab_session', pwd);
      setIsUnlocked(true);
      (await import('@/lib/theme')).applyDarkMode(processed.settings.darkMode);

      // Sign into Supabase in background (non-blocking)
      const email = getStoredEmail();
      if (email) {
        supabase.auth.signInWithPassword({ email, password: pwd }).catch(() => {
          // Silent fail — Supabase session is optional for local usage
        });
      }

      return true;
    } catch {
      return false;
    }
  }, []);

  // Auto-restore session from sessionStorage (survives PWA app-switch reloads)
  useEffect(() => {
    if (autoRestoreAttempted.current || isUnlocked || !isSetup) return;
    autoRestoreAttempted.current = true;
    const cached = sessionStorage.getItem('_ab_session');
    if (cached) {
      unlockWithPassword(cached).catch(() => {
        sessionStorage.removeItem('_ab_session');
      });
    }
  }, [isSetup, isUnlocked, unlockWithPassword]);

  const unlock = useCallback(async (value: string, isPin = false): Promise<boolean> => {
    if (isPin) {
      const pinHash = getPinHash();
      if (!pinHash) return false;
      const validPin = await verifyPassword(value, pinHash);
      if (!validPin) return false;
      const encPwd = getPinEncryptedPassword();
      if (!encPwd) return false;
      try {
        const masterPwd = await decrypt(encPwd, value);
        return unlockWithPassword(masterPwd);
      } catch {
        return false;
      }
    }
    return unlockWithPassword(value);
  }, [unlockWithPassword]);

  const setup = useCallback(async (email: string, pwd: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password: pwd,
      options: { emailRedirectTo: window.location.origin },
    });
    if (error) throw error;
    setStoredEmail(email);
    const hash = await hashPassword(pwd);
    setPasswordHash(hash);
    await saveData(DEFAULT_APP_DATA, pwd);
    setData(DEFAULT_APP_DATA);
    setPassword(pwd);
    setIsSetup(true);
    setIsUnlocked(true);
  }, []);

  const login = useCallback(async (email: string, pwd: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password: pwd });
    if (error) throw error;
    setStoredEmail(email);
    const hash = await hashPassword(pwd);
    setPasswordHash(hash);
    // Load existing local data or start fresh
    let appData: AppData;
    try {
      appData = await loadData(pwd);
    } catch {
      appData = { ...DEFAULT_APP_DATA };
    }
    await saveData(appData, pwd);
    setData(appData);
    setPassword(pwd);
    setIsSetup(true);
    setIsUnlocked(true);
  }, []);

  const lock = useCallback(() => {
    sessionStorage.removeItem('_ab_session');
    setIsUnlocked(false);
    setPassword('');
  }, []);

  const setupPin = useCallback(async (pin: string) => {
    const pinHash = await hashPassword(pin);
    setPinHash(pinHash);
    const encPwd = await encrypt(password, pin);
    setPinEncryptedPassword(encPwd);
    setHasPin(true);
  }, [password]);

  const removePin = useCallback(() => {
    clearPin();
    setHasPin(false);
  }, []);

  const unlockBiometric = useCallback(async (): Promise<boolean> => {
    const credId = getBiometricCredentialId();
    if (!credId) return false;
    const ok = await authenticateBiometric(credId);
    if (!ok) return false;
    const encPwd = getBiometricEncryptedPassword();
    const bioKey = getBiometricKey();
    if (!encPwd || !bioKey) return false;
    try {
      const masterPwd = await decrypt(encPwd, bioKey);
      return unlockWithPassword(masterPwd);
    } catch {
      return false;
    }
  }, [unlockWithPassword]);

  const setupBiometric = useCallback(async () => {
    const credId = await registerBiometric();
    setBiometricCredentialId(credId);
    const randomBytes = crypto.getRandomValues(new Uint8Array(32));
    const bioKey = btoa(String.fromCharCode(...randomBytes));
    setBiometricKey(bioKey);
    const encPwd = await encrypt(password, bioKey);
    setBiometricEncryptedPassword(encPwd);
    setHasBio(true);
  }, [password]);

  const removeBiometric = useCallback(() => {
    clearBiometric();
    setHasBio(false);
  }, []);

  const updateData = useCallback(async (updater: (prev: AppData) => AppData) => {
    setData(prev => {
      const next = updater(prev);
      saveData(next, password).catch(err => {
        if (import.meta.env.DEV) {
          console.error('Failed to save data:', err);
        }
      });
      return next;
    });
  }, [password]);

  const updateStoredEmail = useCallback((newEmail: string) => {
    setStoredEmail(newEmail);
    setStoredEmailState(newEmail);
  }, []);

  return (
    <AuthContext.Provider value={{
      isSetup, isUnlocked, data, unlock, unlockBiometric, setup, login, lock, updateData,
      password, setupPin, removePin, hasPin, setupBiometric, removeBiometric, hasBio,
      supabaseSession, storedEmail, updateStoredEmail,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
